import React, { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { getISTDateInputValue, getISTDateOnlyTimestamp } from "../utils/dateTime";

const BASE_URL = "/v1";
const APPOINTMENT_STATUSES = [
    "BOOKED",
    "PENDING",
    "ASSIGNED",
    "IN_PROGRESS",
    "COMPLETED",
    "CANCELLED"
];
const OPEN_STATUSES = new Set(["IN_PROGRESS", "PENDING"]);
const OPEN_APPOINTMENTS_PAGE_SIZE = 100;
const COMPLETED_CASES_PAGE_SIZE = 100;

const getAppointmentAgeDays = (appointment) => {
    const value = appointment.appointmentDate || appointment.createdAt || appointment.createdDate;
    if (!value) return null;

    const appointmentDay = getISTDateOnlyTimestamp(value);
    const todayDay = getISTDateOnlyTimestamp(new Date());
    if (appointmentDay === null || todayDay === null) return null;
    return Math.max(0, Math.floor((todayDay - appointmentDay) / 86400000));
};

const buildWhatsAppUrl = (mobile) => {
    const digits = String(mobile || "").replace(/\D/g, "");
    if (!digits) return "";
    return `https://wa.me/${digits}?text=`;
};

function DoctorAppointments({ onAddPrescription, onViewHistory, doctors, onError }) {
    const [showPrescriptionModal, setShowPrescriptionModal] = useState(false);
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [completedCaseCount, setCompletedCaseCount] = useState(null);
    const [completedCaseCountLoading, setCompletedCaseCountLoading] = useState(true);
    const [completedCaseCountError, setCompletedCaseCountError] = useState(false);
    const [completedCaseCountDateFiltered, setCompletedCaseCountDateFiltered] = useState(false);
    const [message, setMessage] = useState("");
    const [messageType, setMessageType] = useState("");
    const [openAppointments, setOpenAppointments] = useState([]);
    const [openAppointmentsLoading, setOpenAppointmentsLoading] = useState(true);
    const [openAppointmentsError, setOpenAppointmentsError] = useState("");
    const [slaDays, setSlaDays] = useState(2);
    const [showSlaBreached, setShowSlaBreached] = useState(false);

    const [showAssignModal, setShowAssignModal] = useState(false);
    const [selectedAppointmentId, setSelectedAppointmentId] = useState(null);
    const [selectedDoctorId, setSelectedDoctorId] = useState("");
    const [assigning, setAssigning] = useState(false);
    const [editingAppointmentId, setEditingAppointmentId] = useState(null);
    const [editForm, setEditForm] = useState(null);
    const [savingEdit, setSavingEdit] = useState(false);

    // 🔍 Filters
    const [petName, setPetName] = useState("");
    const [mobile, setMobile] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    // 📄 Pagination
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // 🔽 Sorting
    const [sortField, setSortField] = useState("createdAt");
    const [sortDir, setSortDir] = useState("desc");
    const completedCountRequestId = useRef(0);

    const loadCompletedCaseCount = useCallback(async ({
        petName: pet = "",
        mobile: mob = "",
        fromDate: from = "",
        toDate: to = ""
    } = {}) => {
        const requestId = completedCountRequestId.current + 1;
        completedCountRequestId.current = requestId;

        try {
            setCompletedCaseCountLoading(true);
            setCompletedCaseCountError(false);
            setCompletedCaseCountDateFiltered(Boolean(from || to));
            const token = localStorage.getItem("token");
            let countFromDate = from;
            let countToDate = to;

            if (!countFromDate && !countToDate) {
                const [year, month] = getISTDateInputValue().split("-");
                countFromDate = `${year}-${month}-01`;
                countToDate = `${year}-${month}-${String(new Date(Number(year), Number(month), 0).getDate()).padStart(2, "0")}`;
            }

            let totalPages = 1;
            let completedCount = 0;

            for (let currentPage = 0; currentPage < totalPages; currentPage += 1) {
                const query = new URLSearchParams({
                    page: currentPage,
                    size: COMPLETED_CASES_PAGE_SIZE,
                    petName: pet,
                    mobile: mob,
                    fromDate: countFromDate,
                    toDate: countToDate,
                    sort: "createdAt,desc"
                });
                const response = await fetch(`${BASE_URL}/appointments/search?${query}`, {
                    headers: { Authorization: "Bearer " + token }
                });

                if (!response.ok) {
                    throw new Error(`Unable to load completed case count (${response.status})`);
                }

                const data = await response.json();
                if (!Array.isArray(data?.content)) {
                    throw new Error("The completed cases response was not in the expected format.");
                }

                completedCount += data.content.filter((appointment) =>
                    String(appointment.status || "").toUpperCase() === "COMPLETED"
                ).length;
                totalPages = Math.max(1, Number(data.totalPages) || 1);
            }

            if (requestId === completedCountRequestId.current) {
                setCompletedCaseCount(completedCount);
            }
        } catch (err) {
            console.error("Unable to load completed case count", err);
            if (requestId === completedCountRequestId.current) {
                setCompletedCaseCount(null);
                setCompletedCaseCountError(true);
            }
        } finally {
            if (requestId === completedCountRequestId.current) {
                setCompletedCaseCountLoading(false);
            }
        }
    }, []);

    const loadOpenAppointments = useCallback(async () => {
        try {
            setOpenAppointmentsLoading(true);
            setOpenAppointmentsError("");
            const token = localStorage.getItem("token");
            const results = [];
            let totalPages = 1;

            for (let currentPage = 0; currentPage < totalPages; currentPage += 1) {
                const query = new URLSearchParams({
                    page: currentPage,
                    size: OPEN_APPOINTMENTS_PAGE_SIZE,
                    petName: "",
                    mobile: "",
                    fromDate: "",
                    toDate: "",
                    sort: "createdAt,asc"
                });
                const response = await fetch(`${BASE_URL}/appointments/search?${query}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (!response.ok) {
                    throw new Error(`Unable to load open appointments (${response.status})`);
                }

                const data = await response.json();
                if (!Array.isArray(data?.content)) {
                    throw new Error("The open appointments response was not in the expected format.");
                }

                results.push(...data.content.filter((appointment) =>
                    OPEN_STATUSES.has(String(appointment.status || "").toUpperCase())
                ));
                totalPages = Math.max(1, Number(data.totalPages) || 1);
            }

            setOpenAppointments(results);
        } catch (err) {
            console.error("Unable to load open appointments", err);
            setOpenAppointmentsError("Open appointment counts could not be loaded.");
        } finally {
            setOpenAppointmentsLoading(false);
        }
    }, []);

    const fetchAppointments = () => {
        fetchAppointmentsWithFilters();
        loadOpenAppointments();
    };

    const openPrescriptionModal = (appointment) => {
        setSelectedAppointment(appointment);
        setShowPrescriptionModal(true);
    };

    useEffect(() => {
        fetchAppointments();
    }, [page, sortField, sortDir]);

    useEffect(() => {
        loadCompletedCaseCount();
    }, [loadCompletedCaseCount]);

    useEffect(() => {
        const interval = setInterval(loadOpenAppointments, 30 * 60 * 1000);
        return () => clearInterval(interval);
    }, [loadOpenAppointments]);

    const handleSearch = () => {
        setPage(0);
        fetchAppointments();
        loadCompletedCaseCount({ petName, mobile, fromDate, toDate });
    };

    const toggleSort = (field) => {
        if (sortField === field) {
            setSortDir(sortDir === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortDir("asc");
        }
    };

    const updateStatus = async (appointmentId, status) => {
        try {
            const token = localStorage.getItem("token");
            setMessage("");
            setMessageType("");

            await fetch(`${BASE_URL}/appointments/${appointmentId}/status`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + token
                },
                body: JSON.stringify({ status })
            });

            fetchAppointments();
            loadCompletedCaseCount({ petName, mobile, fromDate, toDate });

        } catch (err) {
            console.error(err);
            setMessage("Failed to update status");
            setMessageType("danger");
        }
    };

    const assignToDoctor = async () => {
        try {
            setAssigning(true);
            const token = localStorage.getItem("token");
            setMessage("");
            setMessageType("");

            const res = await fetch(`${BASE_URL}/appointments/${selectedAppointmentId}/assign`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + token
                },
                body: JSON.stringify({
                    doctorId: selectedDoctorId
                })
            });

            if (!res.ok) {
                throw new Error("Failed to assign appointment");
            }

            setShowAssignModal(false);
            setSelectedDoctorId("");
            fetchAppointments();

        } catch (err) {
            console.error(err);
            setMessage("Failed to assign");
            setMessageType("danger");
        } finally {
            setAssigning(false);
        }
    };

    const unassign = async (appointmentId) => {
        try {
            const token = localStorage.getItem("token");
            setMessage("");
            setMessageType("");

            await fetch(`${BASE_URL}/appointments/${appointmentId}/assign`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + token
                },
                body: JSON.stringify({
                    doctorId: null   // 🔥 unassign
                })
            });

            fetchAppointments();

        } catch (err) {
            console.error(err);
            setMessage("Failed to unassign");
            setMessageType("danger");
        }
    };

    const assignToMe = async (appointmentId) => {
        try {
            setAssigning(true);
            const token = localStorage.getItem("token");
            const user = JSON.parse(localStorage.getItem("user"));
            setMessage("");
            setMessageType("");

            const res = await fetch(`${BASE_URL}/appointments/${appointmentId}/assign`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + token
                },
                body: JSON.stringify({
                    doctorId: user?.doc?.id
                })
            });

            if (!res.ok) {
                throw new Error("Failed to assign appointment");
            }

            // 🔥 refresh after assign
            fetchAppointments();

        } catch (err) {
            console.error(err);
            setMessage("Failed to assign");
            setMessageType("danger");
        } finally {
            setAssigning(false);
        }
    };

    const startEditingAppointment = (appointment) => {
        setEditingAppointmentId(appointment.id);
        setEditForm({
            petName: appointment.petName || "",
            reason: appointment.reason || "",
            status: appointment.status || ""
        });
        setMessage("");
        setMessageType("");
    };

    const cancelEditingAppointment = () => {
        setEditingAppointmentId(null);
        setEditForm(null);
    };

    const saveAppointmentEdits = async (appointmentId) => {
        if (!editForm) return;

        try {
            setSavingEdit(true);
            setMessage("");
            setMessageType("");

            const token = localStorage.getItem("token");
            const res = await fetch(`${BASE_URL}/appointments/${appointmentId}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + token
                },
                body: JSON.stringify({
                    petName: editForm.petName,
                    reason: editForm.reason,
                    status: editForm.status
                })
            });

            if (!res.ok) {
                throw new Error("Failed to update appointment");
            }

            setAppointments((previous) =>
                previous.map((appointment) =>
                    appointment.id === appointmentId
                        ? { ...appointment, ...editForm }
                        : appointment
                )
            );
            setEditingAppointmentId(null);
            setEditForm(null);
            setMessage("Appointment updated successfully");
            setMessageType("success");
            loadOpenAppointments();
            loadCompletedCaseCount({ petName, mobile, fromDate, toDate });
        } catch (err) {
            console.error(err);
            setMessage("Failed to update appointment");
            setMessageType("danger");
        } finally {
            setSavingEdit(false);
        }
    };

    const user = JSON.parse(localStorage.getItem("user"));
    const doctorId = user?.doc?.id;

    const clearFilters = () => {

        setPetName("");
        setMobile("");
        setFromDate("");
        setToDate("");

        setSortField("createdAt");
        setSortDir("desc");

        setPage(0);

        // 🔥 direct clean fetch
        fetchAppointmentsWithFilters({
            petName: "",
            mobile: "",
            fromDate: "",
            toDate: "",
            page: 0,
            sortField: "createdAt",
            sortDir: "desc"
        });
        loadCompletedCaseCount({
            petName: "",
            mobile: "",
            fromDate: "",
            toDate: ""
        });
    };

    const fetchAppointmentsWithFilters = async ({
        petName: pet = petName,
        mobile: mob = mobile,
        fromDate: from = fromDate,
        toDate: to = toDate,
        page: currentPage = page,
        sortField: sf = sortField,
        sortDir: sd = sortDir
    } = {}) => {

        try {

            setLoading(true);

            const token = localStorage.getItem("token");

            const query = new URLSearchParams({
                page: currentPage,
                size: 10,
                petName: pet,
                mobile: mob,
                fromDate: from,
                toDate: to,
                sort: `${sf},${sd}`
            });

            const res = await fetch(
                `${BASE_URL}/appointments/search?${query}`,
                {
                    headers: {
                        Authorization: "Bearer " + token
                    }
                }
            );

            const data = await res.json();

            setAppointments(data.content || []);
            setTotalPages(data.totalPages || 0);

        } catch (err) {

            console.error(err);
            onError?.("danger", "Cannot load appointments right now. Please check the backend server.");

        } finally {

            setLoading(false);
        }
    };

    const openAppointmentsWithAge = openAppointments
        .map((appointment) => ({ ...appointment, ageDays: getAppointmentAgeDays(appointment) }))
        .sort((a, b) => (b.ageDays ?? -1) - (a.ageDays ?? -1));
    const inProgressCount = openAppointments.filter((appointment) =>
        String(appointment.status || "").toUpperCase() === "IN_PROGRESS"
    ).length;
    const pendingCount = openAppointments.filter((appointment) =>
        String(appointment.status || "").toUpperCase() === "PENDING"
    ).length;
    const slaBreachedAppointments = openAppointmentsWithAge.filter((appointment) =>
        appointment.ageDays !== null && appointment.ageDays >= slaDays
    );

    return (
        <div id="doctor-appointments" className="card mt-4 p-3">
            <h5 className="mb-2">Cases</h5>
            <div className="mb-3" aria-live="polite">
                <strong>
                    {completedCaseCountDateFiltered ? "Completed cases for selected dates" : "Completed cases this month"}:
                </strong>{" "}
                {completedCaseCountLoading
                    ? "Loading…"
                    : completedCaseCountError
                        ? "Unavailable"
                        : completedCaseCount ?? 0}
            </div>

            <div className="case-attention-bar" aria-label="Open case summary">
                <div className="case-attention-counts" aria-live="polite">
                    <span className="case-attention-pill">
                        In progress <strong>{openAppointmentsLoading ? "…" : inProgressCount}</strong>
                    </span>
                    <span className="case-attention-pill">
                        Pending <strong>{openAppointmentsLoading ? "…" : pendingCount}</strong>
                    </span>
                    <span className="case-attention-pill case-attention-breached">
                        <AlertTriangle size={14} />
                        SLA breached <strong>{openAppointmentsLoading ? "…" : slaBreachedAppointments.length}</strong>
                    </span>
                </div>

                <div className="case-attention-controls">
                    <label className="case-sla-select">
                        SLA
                        <select
                            className="form-select form-select-sm"
                            value={slaDays}
                            onChange={(event) => setSlaDays(Number(event.target.value))}
                            aria-label="SLA threshold in days"
                        >
                            {[1, 2, 3, 5, 7, 14].map((days) => (
                                <option key={days} value={days}>{days}d</option>
                            ))}
                        </select>
                    </label>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => setShowSlaBreached((visible) => !visible)}
                        aria-expanded={showSlaBreached}
                    >
                        {showSlaBreached ? "Hide breached" : "Show breached"}
                    </button>
                </div>
            </div>

            {openAppointmentsError && (
                <div className="small text-danger mb-2" role="alert">{openAppointmentsError}</div>
            )}

            {showSlaBreached && (
                <div className="case-breached-list mb-3">
                    {slaBreachedAppointments.length === 0 ? (
                        <span className="text-muted small">No open cases have breached this SLA.</span>
                    ) : (
                        slaBreachedAppointments.map((appointment, index) => (
                            <div className="case-breached-row" key={appointment.id || `breached-${index}`}>
                                <strong>{appointment.petName || "Unnamed pet"}</strong>
                                <span className="badge bg-danger">{String(appointment.status).replace(/_/g, " ")}</span>
                                <span>{appointment.ageDays} {appointment.ageDays === 1 ? "day" : "days"} old</span>
                                <span>{appointment.doctorName || "Unassigned"}</span>
                            </div>
                        ))
                    )}
                </div>
            )}

            {message && (
                <div className={`alert alert-${messageType || "info"} py-2`}>
                    {message}
                </div>
            )}

            {/* 🔍 FILTERS */}
            <div className="row g-2 mb-3">

                <div className="col-md-3">
                    <input
                        className="form-control"
                        placeholder="Pet Name"
                        value={petName}
                        onChange={(e) => setPetName(e.target.value)}
                    />
                </div>

                <div className="col-md-3">
                    <input
                        className="form-control"
                        placeholder="Owner Mobile"
                        value={mobile}
                        onChange={(e) => setMobile(e.target.value)}
                    />
                </div>

                <div className="col-md-2">
                    <input
                        type="date"
                        className="form-control"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                    />
                </div>

                <div className="col-md-2">
                    <input
                        type="date"
                        className="form-control"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                    />
                </div>

                <div className="col-md-1">
                    <button
                        className="btn btn-primary w-100"
                        onClick={handleSearch}
                    >
                        Search
                    </button>
                </div>

                <div className="col-md-1">
                    <button
                        className="btn btn-outline-secondary w-100"
                        onClick={clearFilters}
                    >
                        Clear
                    </button>
                </div>

            </div>

            {/* 📋 TABLE */}
            {loading ? (
                <div className="d-flex justify-content-center align-items-center py-5">
                    <div className="text-center">
                        <div className="spinner-border" role="status"></div>
                        <div className="mt-2">Loading...</div>
                    </div>
                </div>
            ) : (
                <div className="table-responsive">
                    <table className="table table-bordered table-sm">
                        <thead className="table-light">
                            <tr>
                                <th onClick={() => toggleSort("appointmentDate")} style={{ cursor: "pointer" }}>
                                    Date
                                </th>
                                <th>Time</th>
                                <th onClick={() => toggleSort("petName")} style={{ cursor: "pointer" }}>
                                    Pet
                                </th>
                                <th>Species</th>
                                <th>Mobile</th>
                                <th>Reason</th>
                                <th>Status</th>
                                <th>Doctor</th>
                                {user?.role !== 3 && (
                                    <th>Action</th>
                                )}
                                <th>Prescription</th>
                            </tr>
                        </thead>

                        <tbody>
                            {appointments.map((a) => (

                                <tr key={a.id}>
                                    <td>{a.appointmentDate}</td>
                                    <td>{a.timeSlot}</td>
                                    <td>
                                        {editingAppointmentId === a.id && editForm ? (
                                            <input
                                                className="form-control form-control-sm"
                                                value={editForm.petName}
                                                onChange={(e) => setEditForm({ ...editForm, petName: e.target.value })}
                                                aria-label="Pet name"
                                            />
                                        ) : a.petName}
                                    </td>
                                    <td>
                                         {a.species}
                                    </td>
                                    <td style={{ whiteSpace: "nowrap" }}>
                                        {String(a.mobileNumber || a.mobile || "").trim() ? (
                                            <a
                                                href={buildWhatsAppUrl(a.mobileNumber || a.mobile)}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-decoration-none"
                                                title="Chat on WhatsApp"
                                            >
                                                {a.mobileNumber || a.mobile}
                                            </a>
                                        ) : (
                                            "-"
                                        )}
                                    </td>
                                    <td style={editingAppointmentId === a.id ? {} : {
                                        maxWidth: "320px",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis"
                                    }}>
                                        {editingAppointmentId === a.id && editForm ? (
                                                                    <input
                                                                        className="form-control form-control-sm"
                                                                        value={editForm.reason}
                                                                        onChange={(e) => setEditForm({ ...editForm, reason: e.target.value })}
                                                                        aria-label="Reason"
                                                                    />
                                        ) : (
                                                                    <span title={a.reason}>{a.reason}</span>
                                        )}
                                    </td>

                                    <td style={{ whiteSpace: "nowrap" }}>
                                        <div className="d-flex flex-nowrap align-items-center gap-1">
                                                                    {editingAppointmentId === a.id && editForm ? (
                                                                        <select
                                                                            className="form-select form-select-sm"
                                                                            value={editForm.status}
                                                                            onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                                                                            aria-label="Appointment status"
                                                                        >
                                                                            {!APPOINTMENT_STATUSES.includes(editForm.status) && (
                                                                                <option value={editForm.status}>{editForm.status}</option>
                                                                            )}
                                                                            {APPOINTMENT_STATUSES.map((status) => (
                                                                                <option key={status} value={status}>{status}</option>
                                                                            ))}
                                                                        </select>
                                                                    ) : (
                                                                        <span className={`badge ${a.status === "COMPLETED" ? "bg-success" :
                                                                            a.status === "IN_PROGRESS" ? "bg-info" :
                                                                                a.status === "ASSIGNED" ? "bg-primary" :
                                                                                    a.status === "PENDING" ? "bg-warning" :
                                                                                        a.status === "BOOKED" ? "bg-secondary" :
                                                                                            a.status === "CANCELLED" ? "bg-danger" :
                                                                                                "bg-dark"
                                                                        }`}>
                                                                    {a.status}
                                                                        </span>
                                                                    )}
                                        </div>
                                    </td>

                                    <td>{a.doctorName || "-"}</td>

                                    <td style={{ whiteSpace: "nowrap" }}>
                                        <div className="d-flex flex-nowrap align-items-center gap-1">

                                        {editingAppointmentId === a.id ? (
                                            <>
                                                <button
                                                    className="btn btn-sm btn-success"
                                                    onClick={() => saveAppointmentEdits(a.id)}
                                                    disabled={savingEdit}
                                                >
                                                    {savingEdit ? "Saving..." : "Save"}
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-secondary"
                                                    onClick={cancelEditingAppointment}
                                                    disabled={savingEdit}
                                                >
                                                    Cancel
                                                </button>
                                            </>
                                        ) : (
                                            <button
                                                className="btn btn-sm btn-outline-primary"
                                                onClick={() => startEditingAppointment(a)}
                                                disabled={editingAppointmentId !== null}
                                            >
                                                Edit
                                            </button>
                                        )}

                                        {/* 🔹 ADMIN → Assign to any doctor */}
                                        {editingAppointmentId !== a.id && user?.role === 1 && !a.doctorId && (
                                            <button
                                                className="btn btn-sm btn-primary"
                                                onClick={() => {
                                                    setSelectedAppointmentId(a.id);
                                                    setShowAssignModal(true);
                                                }}
                                            >
                                                Assign
                                            </button>
                                        )}

                                        {/* 🔹 DOCTOR → Assign to Me */}
                                        {editingAppointmentId !== a.id && user?.role === 2 && !a.doctorId && (
                                            <button
                                                className="btn btn-sm btn-success"
                                                onClick={() => assignToMe(a.id)}
                                                disabled={assigning}
                                            >
                                                {assigning ? "Assigning..." : "Assign to Me"}
                                            </button>
                                        )}

                                        {/* 🔹 Assigned to me */}
                                        {editingAppointmentId !== a.id && a.doctorId === doctorId && (
                                            <>
                                                {a.status === "ASSIGNED" && (
                                                    <button
                                                        className="btn btn-sm btn-info"
                                                        onClick={() => updateStatus(a.id, "IN_PROGRESS")}
                                                    >
                                                        Start
                                                    </button>
                                                )}

                                                {a.status === "IN_PROGRESS" && (
                                                    <button
                                                        className="btn btn-sm btn-success"
                                                        onClick={() => updateStatus(a.id, "COMPLETED")}
                                                    >
                                                        Complete
                                                    </button>
                                                )}

                                                {(a.status === "ASSIGNED" || a.status === "IN_PROGRESS") && (
                                                    <button
                                                        className="btn btn-sm btn-danger"
                                                        onClick={() => unassign(a.id)}
                                                    >
                                                        Unassign
                                                    </button>
                                                )}
                                            </>
                                        )}

                                        {/* 🔹 Assigned to someone else */}
                                        {editingAppointmentId !== a.id && a.doctorId && a.doctorId !== doctorId && (
                                            <button className="btn btn-sm btn-secondary" disabled>
                                                Assigned
                                            </button>
                                        )}

                                        </div>
                                    </td>
                                    <td style={{ whiteSpace: "nowrap" }}>
                                        <div className="d-flex flex-nowrap align-items-center gap-1">

                                        {/* ✅ ADD */}
                                        {a.doctorId === doctorId && a.status === "IN_PROGRESS" && (
                                            <button
                                                className="btn btn-sm btn-outline-primary"
                                                onClick={() => onAddPrescription(a)}
                                            >
                                                Add
                                            </button>
                                        )}

                                        {/* ✅ VIEW HISTORY (ALWAYS AVAILABLE) */}
                                        <button
                                            className="btn btn-sm btn-outline-primary"
                                            onClick={() =>
                                                onViewHistory({
                                                    id: a.petId,
                                                    name: a.petName
                                                }, {
                                                    appointmentId: a.id,
                                                    doctorId: a.doctorId,
                                                    status: a.status,
                                                    doctorName: a.doctorName
                                                })
                                            }
                                        >
                                            View
                                        </button>

                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* 📄 PAGINATION */}
            <div className="d-flex justify-content-between mt-2">
                <button
                    className="btn btn-secondary btn-sm"
                    disabled={page === 0}
                    onClick={() => setPage(prev => prev - 1)}
                >
                    Prev
                </button>

                <span>Page {page + 1} of {totalPages}</span>

                <button
                    className="btn btn-secondary btn-sm"
                    disabled={page + 1 >= totalPages}
                    onClick={() => setPage(prev => prev + 1)}
                >
                    Next
                </button>
            </div>
            {showAssignModal && (

                <>
                    <div className="modal-backdrop fade show"></div>

                    <div className="modal show fade d-block">
                        <div className="modal-dialog">
                            <div className="modal-content">

                                <div className="modal-header">
                                    <h5>Select Doctor</h5>
                                    <button
                                        className="btn-close"
                                        onClick={() => setShowAssignModal(false)}
                                        disabled={assigning}
                                    />
                                </div>

                                <div className="modal-body">
                                    <select
                                        className="form-select"
                                        value={selectedDoctorId}
                                        onChange={(e) => setSelectedDoctorId(e.target.value)}
                                        disabled={assigning}
                                    >
                                        <option value="">

                                            {doctors.length === 0 ? "Loading doctors..." : "Select Doctor"}
                                        </option>

                                        {doctors
                                            .filter(doc => doc.isActive)   // 🔥 ADD THIS
                                            .map((doc) => (
                                                <option key={doc.id} value={doc.id}>
                                                    Dr. {doc.fName} {doc.lName}
                                                </option>
                                            ))}
                                    </select>
                                </div>

                                <div className="modal-footer">
                                    <button
                                        className="btn btn-secondary"
                                        onClick={() => setShowAssignModal(false)}
                                        disabled={assigning}
                                    >
                                        Cancel
                                    </button>

                                    <button
                                        className="btn btn-primary"
                                        disabled={!selectedDoctorId || assigning}
                                        onClick={assignToDoctor}
                                    >
                                        {assigning ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Assigning...
                                            </>
                                        ) : "Assign"}
                                    </button>
                                </div>

                            </div>
                        </div>
                    </div>
                </>
            )}

        </div>
    );
}

export default DoctorAppointments;
